import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Mail, MapPin, Phone, Plus, X } from "lucide-react";
import { showToast } from "reducers/toastReducer";
import { getApiUrl } from "data/services/apiConstants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "../components/ui/select";
import { Section, PageHero } from "../components/site/section";
import { PageCta } from "../components/site/page-cta";
const categories = [
    "Violation Resolution",
    "Pricing & Sales",
    "Billing & Refunds",
    "Technical Support",
    "Airtable Integration Request",
    "Comprehensive Property Report Request",
    "Property Status Report Request",
    "Feature Request",
    "API Access",
    "Something else",
];
const addressCategories = [
    "Airtable Integration Request",
    "Comprehensive Property Report Request",
    "Property Status Report Request",
];

const details = [
    { icon: Mail, label: "Email", value: "admin@violerts.com" },
    { icon: Phone, label: "Phone", value: "(212) 401-6011" },
    { icon: MapPin, label: "Office", value: "130 W 37th St, New York" },
];
function Contact() {
    const category = new URLSearchParams(useLocation().search).get("category") ?? undefined;
    const dispatch = useDispatch();
    const initialCategory = category && categories.includes(category) ? category : undefined;
    const [selectedCategory, setSelectedCategory] = useState(initialCategory);
    const [sending, setSending] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const [addresses, setAddresses] = useState([""]);
    const showAddresses = selectedCategory !== undefined && addressCategories.includes(selectedCategory);
    return (<>
      <PageHero title="Get in touch with us." description="Whether it's two buildings or two thousand, we'll show you what Violerts already knows about them."/>

      <Section>
        <div className="grid gap-12 md:grid-cols-[1.2fr_1fr]">
          <form key={formKey} className="rounded-xl border border-border bg-card p-8 shadow-card" onSubmit={async (e) => {
            // Posts to the API (POST /api/contact), which emails the team with the visitor as Reply-To.
            e.preventDefault();
            if (sending) return;
            const formEl = e.target;
            const form = new FormData(formEl);
            const field = (name) => (form.get(name) || "").toString().trim();
            setSending(true);
            try {
                const response = await fetch(`${getApiUrl()}/api/contact`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Accept: "application/json" },
                    body: JSON.stringify({
                        first_name: field("first-name"),
                        last_name: field("last-name"),
                        email: field("email"),
                        phone: field("phone"),
                        category: field("category") || null,
                        addresses: addresses.map((a) => a.trim()).filter(Boolean),
                        message: field("message"),
                        website: field("website"),
                    }),
                });
                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    const firstError = data.errors ? Object.values(data.errors)[0]?.[0] : null;
                    throw new Error(firstError || data.message || (response.status === 429 ? "Too many messages. Please try again in a few minutes." : "We could not send your message. Please try again."));
                }
                formEl.reset();
                setAddresses([""]);
                setSelectedCategory(undefined);
                setFormKey((key) => key + 1);
                dispatch(showToast({ type: "success", title: "Message sent", body: "Thanks - we'll be in touch within one business day." }));
            }
            catch (error) {
                dispatch(showToast({ type: "error", title: "Message not sent", body: error.message }));
            }
            finally {
                setSending(false);
            }
        }}>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first-name">First Name</Label>
                <Input id="first-name" name="first-name" required/>
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">Last Name</Label>
                <Input id="last-name" name="last-name" required/>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" name="email" type="email" required/>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input id="phone" name="phone" type="tel"/>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="category">Category</Label>
                <Select name="category" className="mt-2" key={`${initialCategory ?? "none"}-${formKey}`} onValueChange={(value) => setSelectedCategory(value)} {...(initialCategory ? { defaultValue: initialCategory } : {})}>
                  <SelectTrigger id="category" className="w-full">
                    <SelectValue placeholder="Select a category"/>
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (<SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {showAddresses ? (<div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="address-0">Address</Label>
                  {addresses.map((address, index) => (<div key={index} className="flex gap-2">
                      <Input id={`address-${index}`} name={`address-${index}`} placeholder="Street address, borough, ZIP" value={address} onChange={(e) => setAddresses((current) => current.map((a, i) => (i === index ? e.target.value : a)))}/>
                      {index === addresses.length - 1 ? (<Button type="button" variant="outline" size="icon" aria-label="Add another address" onClick={() => setAddresses((current) => [...current, ""])}>
                          <Plus className="h-4 w-4"/>
                        </Button>) : (<Button type="button" variant="outline" size="icon" aria-label="Remove address" onClick={() => setAddresses((current) => current.filter((_, i) => i !== index))}>
                          <X className="h-4 w-4"/>
                        </Button>)}
                    </div>))}
                </div>) : null}
            </div>
            <div className="mt-5 space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" name="message" rows={5}/>
            </div>
            {/* Honeypot: invisible to people, tempting to bots. The API drops submissions that fill it. */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0"/>
            <Button type="submit" variant="signal" size="lg" className="mt-6" disabled={sending}>
              {sending ? "Sending…" : "Send message"}
            </Button>
          </form>

          <div>
            <h2 className="text-xl font-semibold">Other ways to reach us</h2>
            <ul className="mt-6 space-y-5">
              {details.map((d) => (<li key={d.label} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-foreground">
                    <d.icon className="h-4 w-4 text-background"/>
                  </span>
                  <div>
                    <p className="text-sm text-muted-foreground">{d.label}</p>
                    <p className="font-medium">{d.value}</p>
                  </div>
                </li>))}
            </ul>
          </div>
        </div>
      </Section>
      <PageCta />
    </>);
}

export default Contact;
